import React, {Component} from 'react';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import debounce from 'lodash.debounce';
import {push} from 'connected-react-router/immutable';
import {t, Trans} from '@lingui/macro';

import {LOGIN} from '../../services/routes';
import {fetch} from '../../services/fetch';
import {setNewPassword, passwordValidationError} from '../../actions/login';
import {loadLanguages} from '@actions/languages';

import {isSettingNewPassword} from '../../reducers/ui';

import SkillupLogo from '../../components/Icons/SkillupLogo';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

import cl from 'classnames';
import s from './NewPassword.css';

import config from 'config';
import {getSuffix} from '@services/utilities';

class NewPassword extends Component {
  constructor(props) {
    super(props);

    const {
      match: {params},
    } = props;
    if (!params || !params.token || !params.email) {
      this.goToLoginPage();
    }

    this.state = {
      isCheckingToken: true,
      password: '',
      confirmPassword: '',
    };
    this.debouncedSend = debounce(props.setNewPassword, 200);
  }

  componentDidMount() {
    const {
      match: {params},
      push,
      loadLanguages,
    } = this.props;
    const {email, token} = params;

    loadLanguages();

    fetch(`${config.api}/auth/checkToken`, {
      method: 'POST',
      data: {
        email,
        token,
      },
    }).then(({data}) => {
      const {code, status, username} = data;

      if (status) {
        this.setState({isCheckingToken: false});
        return;
      }

      if (code === 'INVALID_USER') {
        this.goToLoginPage();
        return;
      }

      if (code === 'INVALID_TOKEN') {
        push({pathname: LOGIN, state: {username}});
      }
    });
  }

  onPasswordChange = event => this.setState({password: event.target.value});
  onConfirmPasswordChange = event => this.setState({confirmPassword: event.target.value});

  onSubmit = event => {
    if (!this.validate()) {
      event.preventDefault();
      return;
    }

    const {
      match: {
        params: {token, email},
      },
    } = this.props;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const {password, confirmPassword} = this.state;
    this.debouncedSend({password, confirmPassword, email, token, timezone});
    event.preventDefault();
  };

  validate = () => {
    const {password, confirmPassword} = this.state;
    const {passwordValidationError} = this.props;

    let errors = [];
    if (!password || password.length < 8) {
      errors.push(
        t({
          id: `new_password.error_8_characters`,
          message: 'La password deve essere lunga almeno 8 caratteri',
        })
      );
    }
    if (!/[a-z]/.test(password)) {
      errors.push(
        t({
          id: `new_password.error_lowercase`,
          message: 'La password deve contenere almeno una lettera minuscola',
        })
      );
    }
    if (!/[A-Z]/.test(password)) {
      errors.push(
        t({
          id: `new_password.error_uppercase`,
          message: 'La password deve contenere almeno una lettera maiuscola',
        })
      );
    }
    if (!/[0-9]/.test(password)) {
      errors.push(
        t({
          id: `new_password.error_number`,
          message: 'La password deve contenere almeno un numero',
        })
      );
    }
    if (confirmPassword !== password) {
      errors.push(
        t({
          id: `new_password.error_no_match`,
          message: 'Le password non coincidono',
        })
      );
    }
    if (errors.length > 0) {
      passwordValidationError(errors.join('. \n'));
      return false;
    }

    return true;
  };

  goToLoginPage = e => {
    this.props.push(LOGIN);

    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  render() {
    const {isSettingNewPassword} = this.props;
    const {password, confirmPassword, isCheckingToken} = this.state;
    const isActive =
      typeof password !== 'undefined' && password.length >= 8 && password === confirmPassword;

    if (isCheckingToken) {
      return <LoadingSpinner delay={500} />;
    }

    return (
      <div className={cl(s.LoginPage)}>
        <SkillupLogo className={s.Logo} negative size="38px" />

        <p className={s.Welcome}>
          <Trans id="new_password.title_set_new_password">Imposta una nuova password</Trans>
        </p>

        <section className={s.LoginForm}>
          <form onSubmit={this.onSubmit}>
            <section>
              <input
                type="password"
                className={s.TextField}
                placeholder={t({
                  id: `new_password.placeholder_password`,
                  message: 'Password',
                })}
                value={password}
                onChange={this.onPasswordChange}
              />
              <input
                type="password"
                className={s.TextField}
                placeholder={t({
                  id: `new_password.placeholder_confirm_password`,
                  message: 'Conferma password',
                })}
                value={confirmPassword}
                onChange={this.onConfirmPasswordChange}
              />
            </section>

            <section className={s.ButtonContainer}>
              <Button
                className={cl(s.SendButton, isActive && s.Active)}
                loading={isSettingNewPassword}
              >
                <span>
                  <Trans id="new_password.button_change_password">Cambia password</Trans>
                </span>
              </Button>
            </section>
          </form>
        </section>

        <div className={s.Info}>
          <p>
            <Trans id="new_password.info_password">
              La password deve essere lunga più di 8 caratteri e contenere almeno una lettera
              minuscola, una maiuscola e un numero
            </Trans>
          </p>
          <a href="#" onClick={this.goToLoginPage}>
            <Trans id="new_password.link_cancel">Annulla</Trans>
          </a>
        </div>

        <p className={s.Copyright}>{`Skillup © ${new Date().getFullYear()} ${getSuffix()}`}</p>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    isSettingNewPassword: isSettingNewPassword(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {setNewPassword, passwordValidationError, push, loadLanguages},
    dispatch
  );
}

export const ConnectedNewPassword = connect(mapStateToProps, mapDispatchToProps)(NewPassword);
